import type {
  PreStepOption,
  RegularStep,
  Step,
  StepProduct,
  StepProductFilterPolicy,
} from 'components/powerstep-drawer';

import { looseMatch } from 'utils/product-property';

type PowerstepFilterContext = {
  sectionFilterAttribute: string;
  sectionManualFilterValue: string;
  steps: Step[];
  triggerValues: Record<string, string>;
};

type StepSlotFilter = {
  attribute?: string;
  manualValue?: string;
};

const defaultPolicy = (): StepProductFilterPolicy => ({
  allow_section_filter: true,
  allow_pre_step_filter: true,
  allow_step_filter: true,
  allow_filter_product_list: true,
  allow_filter_collection: true,
});

const resolveFilterPolicy = (
  policy: StepProductFilterPolicy | undefined,
): StepProductFilterPolicy => ({
  ...defaultPolicy(),
  ...policy,
});

const effectiveTriggerValue = (
  attr: string,
  manualValue: string | undefined,
  triggerValues: Record<string, string>,
): string => {
  if (!attr) return '';
  if (manualValue?.trim()) return manualValue.trim();
  return triggerValues[attr] ?? '';
};

export const primarySlotFilter = (step: RegularStep): StepSlotFilter => ({
  attribute: step.filter_attribute,
  manualValue: step.filter_value,
});

export const secondarySlotFilter = (step: RegularStep): StepSlotFilter => ({
  attribute: step.secondary_filter_attribute,
  manualValue: step.secondary_filter_value,
});

const filteringEnabledForSource = (
  product: StepProduct,
  policy: StepProductFilterPolicy,
): boolean => {
  const source = product.source ?? 'product_list';
  if (source === 'product_list') return policy.allow_filter_product_list !== false;
  if (source === 'collection') return policy.allow_filter_collection !== false;
  return true;
};

const productPassesFilters = (
  product: StepProduct,
  policy: StepProductFilterPolicy,
  slotFilter: StepSlotFilter,
  ctx: PowerstepFilterContext,
  option: PreStepOption | undefined,
): boolean => {
  const resolvedPolicy = resolveFilterPolicy(policy);

  if (!filteringEnabledForSource(product, resolvedPolicy)) return true;

  const sectionTriggerVal =
    resolvedPolicy.allow_section_filter !== false && ctx.sectionFilterAttribute
      ? effectiveTriggerValue(
          ctx.sectionFilterAttribute,
          ctx.sectionManualFilterValue,
          ctx.triggerValues,
        )
      : '';

  const optionAttr = option?.filter_attribute ?? '';
  const optionTriggerVal =
    resolvedPolicy.allow_pre_step_filter !== false && optionAttr
      ? effectiveTriggerValue(optionAttr, option?.filter_value, ctx.triggerValues)
      : '';

  const slotTriggerVal =
    resolvedPolicy.allow_step_filter !== false && slotFilter.attribute
      ? effectiveTriggerValue(slotFilter.attribute, slotFilter.manualValue, ctx.triggerValues)
      : '';

  if (
    sectionTriggerVal &&
    !(
      product.section_filter_value !== '' &&
      looseMatch(product.section_filter_value, sectionTriggerVal)
    )
  )
    return false;

  if (
    optionTriggerVal &&
    !(product.filter_value !== '' && looseMatch(product.filter_value, optionTriggerVal))
  )
    return false;

  if (
    slotTriggerVal &&
    !(product.step_filter_value !== '' && looseMatch(product.step_filter_value, slotTriggerVal))
  )
    return false;

  return true;
};

const stepHasVisibleProduct = (
  step: RegularStep,
  ctx: PowerstepFilterContext,
  option: PreStepOption | undefined,
): boolean => {
  const primaryPolicy = resolveFilterPolicy(step.primary_filter_policy);
  const secondaryPolicy = resolveFilterPolicy(step.secondary_filter_policy);

  if (
    step.primary_products.some((p) =>
      productPassesFilters(p, primaryPolicy, primarySlotFilter(step), ctx, option),
    )
  )
    return true;

  if (
    step.secondary_products.some((p) =>
      productPassesFilters(p, secondaryPolicy, secondarySlotFilter(step), ctx, option),
    )
  )
    return true;

  return false;
};

export const isPreStepOptionVisible = (
  option: PreStepOption,
  ctx: PowerstepFilterContext,
): boolean => {
  for (const step of ctx.steps) {
    if (step.type !== 'step') continue;
    if (stepHasVisibleProduct(step, ctx, option)) return true;
  }
  return false;
};

export const getVisibleSteps = (
  ctx: PowerstepFilterContext,
  option: PreStepOption | undefined,
): Step[] =>
  ctx.steps.filter((step) => {
    if (step.type === 'cutting_service_step') return true;
    return stepHasVisibleProduct(step, ctx, option);
  });

export const filterVisibleStepProducts = (
  products: StepProduct[],
  policy: StepProductFilterPolicy,
  slotFilter: StepSlotFilter,
  ctx: PowerstepFilterContext,
  option: PreStepOption | undefined,
): StepProduct[] =>
  products.filter((p) =>
    productPassesFilters(p, resolveFilterPolicy(policy), slotFilter, ctx, option),
  );

/**
 * Whether the powerstep drawer should open for this trigger product: with a pre-step, at least one
 * option must pass filters; without one, at least one step must remain visible.
 */
export const shouldOpenPowerstep = (
  preStepOptions: PreStepOption[],
  ctx: PowerstepFilterContext,
): boolean => {
  if (preStepOptions.length > 0) {
    return preStepOptions.some((option) => isPreStepOptionVisible(option, ctx));
  }
  return getVisibleSteps(ctx, undefined).length > 0;
};
